(function () {
  'use strict';

  var DEFAULT_CENTER = { lat: 30, lng: 10 };

  function safeText(value) {
    return value == null ? '' : String(value).trim();
  }

  function escapeHtml(value) {
    return safeText(value).replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  function safeUrl(value) {
    var url = safeText(value);
    if (!url) return '';
    if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
    return 'https://' + url.replace(/^\/+/, '');
  }

  function uniqueSorted(values) {
    return values.filter(function (value, index, array) {
      return value && array.indexOf(value) === index;
    }).sort(function (a, b) { return a.localeCompare(b); });
  }

  function distanceInKilometers(origin, destination) {
    var earthRadius = 6371;
    var toRadians = function (degrees) { return degrees * Math.PI / 180; };
    var latitudeDelta = toRadians(destination.lat - origin.lat);
    var longitudeDelta = toRadians(destination.lng - origin.lng);
    var originLatitude = toRadians(origin.lat);
    var destinationLatitude = toRadians(destination.lat);
    var haversine = Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
      Math.cos(originLatitude) * Math.cos(destinationLatitude) *
      Math.sin(longitudeDelta / 2) * Math.sin(longitudeDelta / 2);
    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  function StoreLocator(root) {
    this.root = root;
    this.accentColor = window.getComputedStyle(root).getPropertyValue('--locator-accent').trim() || '#c7a266';
    this.search = root.querySelector('[data-locator-search]');
    this.country = root.querySelector('[data-locator-country]');
    this.city = root.querySelector('[data-locator-city]');
    this.clear = root.querySelector('[data-locator-clear]');
    this.list = root.querySelector('[data-locator-list]');
    this.count = root.querySelector('[data-locator-count]');
    this.empty = root.querySelector('[data-locator-empty]');
    this.mapElement = root.querySelector('[data-locator-map]');
    this.mapStatus = root.querySelector('[data-locator-map-status]');
    this.locationStatus = root.querySelector('[data-locator-location-status]');
    this.locationStatusText = root.querySelector('[data-locator-location-status-text]');
    this.nearbyLimit = Math.max(1, parseInt(root.dataset.nearbyLimit, 10) || 5);
    this.locations = this.readLocations();
    this.filtered = this.locations.slice();
    this.markers = [];
    this.markerById = {};
    this.map = null;
    this.userPosition = null;
    this.userMarker = null;
    this.locationRequestInFlight = false;
    this.locationRetryBound = false;
    this.bind();
    this.populateCountries();
    this.render();
    this.initMap();
    this.requestUserLocation();
  }

  StoreLocator.prototype.readLocations = function () {
    var records = [];
    var scripts = Array.prototype.slice.call(document.querySelectorAll('[data-store-locator-source]'));
    var metaobjectsScript = this.root.querySelector('[data-store-locator-metaobjects]');
    var metaobjects = [];

    try { metaobjects = JSON.parse(metaobjectsScript.textContent || '[]'); } catch (error) { metaobjects = []; }
    if (metaobjects.length) scripts = [metaobjectsScript];

    scripts.forEach(function (script) {
      try { records = records.concat(JSON.parse(script.textContent || '[]')); } catch (error) { /* Ignore invalid data groups. */ }
    });

    return records.filter(function (record) { return safeText(record.name); }).map(function (record, index) {
      record.id = safeText(record.id) || 'location-' + index;
      record.name = safeText(record.name);
      record.country = safeText(record.country);
      record.state = safeText(record.state);
      record.city = safeText(record.city);
      record.district = safeText(record.district);
      record.address = safeText(record.address);
      record.phone = safeText(record.phone);
      record.email = safeText(record.email);
      record.website = safeText(record.website);
      record.instagram = safeText(record.instagram);
      record.hours = safeText(record.hours);
      record.type = safeText(record.type);
      record.notes = safeText(record.notes);
      record.latitude = parseFloat(record.latitude);
      record.longitude = parseFloat(record.longitude);
      return record;
    });
  };

  StoreLocator.prototype.bind = function () {
    var locator = this;
    var refresh = function () { locator.applyFilters(); };
    this.search.addEventListener('input', refresh);
    this.country.addEventListener('change', function () {
      locator.populateCities();
      refresh();
    });
    this.city.addEventListener('change', refresh);
    this.clear.addEventListener('click', function () {
      locator.search.value = '';
      locator.country.value = '';
      locator.city.value = '';
      locator.populateCities();
      locator.applyFilters();
      locator.search.focus();
    });
  };

  StoreLocator.prototype.populateCountries = function () {
    var current = this.country.value;
    var firstOption = this.country.options[0];
    var countries = uniqueSorted(this.locations.map(function (location) { return location.country; }));
    this.country.innerHTML = '';
    this.country.appendChild(firstOption);
    countries.forEach(function (country) {
      var option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      this.country.appendChild(option);
    }, this);
    this.country.value = current;
    this.populateCities();
  };

  StoreLocator.prototype.populateCities = function () {
    var selectedCountry = this.country.value;
    var firstOption = this.city.options[0];
    var cities = uniqueSorted(this.locations.filter(function (location) {
      return !selectedCountry || location.country === selectedCountry;
    }).map(function (location) { return location.city; }));
    this.city.innerHTML = '';
    this.city.appendChild(firstOption);
    cities.forEach(function (city) {
      var option = document.createElement('option');
      option.value = city;
      option.textContent = city;
      this.city.appendChild(option);
    }, this);
    this.city.disabled = !cities.length;
    if (cities.indexOf(this.city.value) === -1) this.city.value = '';
  };

  StoreLocator.prototype.applyFilters = function () {
    var query = this.search.value.trim().toLocaleLowerCase();
    var country = this.country.value;
    var city = this.city.value;
    var useNearbyResults = this.userPosition && !query && !country && !city;
    var matches = this.sortByDistance(this.locations.filter(function (location) {
      var haystack = [location.name, location.country, location.state, location.city, location.district, location.address].join(' ').toLocaleLowerCase();
      return (!country || location.country === country) && (!city || location.city === city) && (!query || haystack.indexOf(query) !== -1);
    }));

    if (useNearbyResults) {
      var nearbyMatches = matches.filter(function (location) { return isFinite(location.distanceKm); });
      this.filtered = nearbyMatches.length ? nearbyMatches.slice(0, this.nearbyLimit) : matches;
    } else {
      this.filtered = matches;
    }
    this.render();
    this.updateMap();
  };

  StoreLocator.prototype.render = function () {
    var locator = this;
    this.list.innerHTML = '';
    this.count.textContent = this.filtered.length;
    this.count.setAttribute('aria-label', this.filtered.length + ' ' + (this.root.dataset.resultsLabel || 'locations found'));
    this.empty.hidden = this.filtered.length > 0;

    this.filtered.forEach(function (location) {
      var card = document.createElement('article');
      card.className = 'store-locator-card';
      card.tabIndex = 0;
      card.dataset.locationId = location.id;
      var locality = [location.district, location.city, location.state, location.country].filter(Boolean).filter(function (value, index, array) { return array.indexOf(value) === index; }).join(', ');
      var links = [];
      var distance = locator.formatDistance(location.distanceKm);

      if (location.phone) links.push('<a href="tel:' + escapeHtml(location.phone.replace(/[^+\d]/g, '')) + '">Phone</a>');
      if (location.email) links.push('<a href="mailto:' + escapeHtml(location.email) + '">Email</a>');
      if (location.website) links.push('<a href="' + escapeHtml(safeUrl(location.website)) + '" target="_blank" rel="noopener">Website</a>');
      if (location.instagram) links.push('<a href="' + escapeHtml(safeUrl(location.instagram)) + '" target="_blank" rel="noopener">Instagram</a>');
      if (locator.locationAddress(location)) links.push('<button type="button" data-directions>' + escapeHtml(locator.root.dataset.directionsLabel || 'Directions') + '</button>');

      card.innerHTML =
        (location.type ? '<span class="store-locator-card__type">' + escapeHtml(location.type) + '</span>' : '') +
        '<h2>' + escapeHtml(location.name) + '</h2>' +
        (distance ? '<p class="store-locator-card__distance"><span aria-hidden="true">⌖</span>' + escapeHtml(distance) + '</p>' : '') +
        (locality ? '<p class="store-locator-card__location">' + escapeHtml(locality) + '</p>' : '') +
        (location.address ? '<p class="store-locator-card__address">' + escapeHtml(location.address) + '</p>' : '') +
        (location.hours ? '<p class="store-locator-card__hours"><strong>Hours:</strong> ' + escapeHtml(location.hours) + '</p>' : '') +
        (location.notes ? '<p class="store-locator-card__notes">' + escapeHtml(location.notes) + '</p>' : '') +
        (links.length ? '<div class="store-locator-card__links">' + links.join('') + '</div>' : '') +
        '<span class="store-locator-card__chevron" aria-hidden="true">›</span>';

      var activate = function () { locator.focusLocation(location, card); };
      card.addEventListener('click', function (event) {
        if (event.target.closest('a')) return;
        if (event.target.closest('[data-directions]')) {
          window.open(locator.directionsUrl(location), '_blank', 'noopener');
          return;
        }
        activate();
      });
      card.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); }
      });
      locator.list.appendChild(card);
    });
  };

  StoreLocator.prototype.setLocationStatus = function (message, state) {
    if (!this.locationStatus || !this.locationStatusText) return;
    this.locationStatus.hidden = !message;
    this.locationStatus.classList.remove('is-locating', 'is-success', 'is-unavailable');
    if (state) this.locationStatus.classList.add('is-' + state);
    this.locationStatusText.textContent = message || '';
  };

  StoreLocator.prototype.sortByDistance = function (locations) {
    return locations.slice().sort(function (first, second) {
      var firstDistance = isFinite(first.distanceKm) ? first.distanceKm : Infinity;
      var secondDistance = isFinite(second.distanceKm) ? second.distanceKm : Infinity;
      if (firstDistance !== secondDistance) return firstDistance - secondDistance;
      return first.name.localeCompare(second.name);
    });
  };

  StoreLocator.prototype.formatDistance = function (distance) {
    if (!isFinite(distance)) return '';
    if (distance < 1) return Math.max(1, Math.round(distance * 1000)) + ' ' + (this.root.dataset.metersAwayLabel || 'm away');
    var rounded = distance < 100 ? Math.round(distance * 10) / 10 : Math.round(distance);
    return rounded + ' ' + (this.root.dataset.kilometersAwayLabel || 'km away');
  };

  StoreLocator.prototype.requestUserLocation = function () {
    var locator = this;
    if (this.locationRequestInFlight || this.userPosition) return;
    if (!navigator.geolocation || !window.isSecureContext) {
      this.setLocationStatus(this.root.dataset.locationUnavailableLabel, 'unavailable');
      return;
    }

    this.locationRequestInFlight = true;
    this.setLocationStatus(this.root.dataset.locatingLabel || 'Finding stores near you...', 'locating');
    navigator.geolocation.getCurrentPosition(function (position) {
      locator.locationRequestInFlight = false;
      locator.userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
      locator.locations.forEach(function (location) {
        var locationPosition = locator.locationPosition(location);
        location.distanceKm = locationPosition ? distanceInKilometers(locator.userPosition, locationPosition) : NaN;
      });
      locator.setLocationStatus(locator.root.dataset.nearbyLabel || 'Locations are sorted by distance from you.', 'success');
      locator.applyFilters();
    }, function (error) {
      locator.locationRequestInFlight = false;
      locator.setLocationStatus(locator.root.dataset.locationUnavailableLabel || 'Use search or filters to explore all locations.', 'unavailable');
      if (!error || error.code !== 1) locator.bindLocationRetry();
    }, {
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 300000
    });
  };

  StoreLocator.prototype.bindLocationRetry = function () {
    if (this.locationRetryBound || this.userPosition) return;
    var locator = this;
    this.locationRetryBound = true;

    var retry = function () {
      document.removeEventListener('pointerdown', retry, true);
      document.removeEventListener('keydown', retry, true);
      locator.locationRetryBound = false;
      locator.requestUserLocation();
    };

    document.addEventListener('pointerdown', retry, { once: true, capture: true });
    document.addEventListener('keydown', retry, { once: true, capture: true });
  };

  StoreLocator.prototype.initMap = function () {
    if (!window.L) {
      this.mapStatus.classList.add('is-error');
      this.mapStatus.querySelector('span:last-child').textContent = this.root.dataset.mapUnavailableLabel;
      return;
    }

    this.map = window.L.map(this.mapElement, {
      center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      zoom: 2,
      minZoom: 2,
      zoomControl: true,
      scrollWheelZoom: false,
      worldCopyJump: true
    });

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
    }).addTo(this.map);

    this.mapStatus.classList.add('is-hidden');
    this.updateMap();
  };

  StoreLocator.prototype.locationAddress = function (location) {
    return [location.address, location.district, location.city, location.state, location.country].filter(Boolean).join(', ');
  };

  StoreLocator.prototype.locationPosition = function (location) {
    if (isFinite(location.latitude) && isFinite(location.longitude)) return { lat: location.latitude, lng: location.longitude };
    return null;
  };

  StoreLocator.prototype.clearMarkers = function () {
    this.markers.forEach(function (marker) { marker.remove(); });
    this.markers = [];
    this.markerById = {};
  };

  StoreLocator.prototype.updateUserMarker = function () {
    if (!this.map || !this.userPosition) return;
    if (this.userMarker) this.userMarker.remove();
    this.userMarker = window.L.circleMarker([this.userPosition.lat, this.userPosition.lng], {
      radius: 7,
      color: this.accentColor,
      weight: 3,
      fillColor: '#fffdf9',
      fillOpacity: 1
    }).addTo(this.map).bindTooltip(this.root.dataset.yourLocationLabel || 'Your location', { direction: 'top', offset: [0, -8] });
  };

  StoreLocator.prototype.addMarker = function (location, position, bounds) {
    var locator = this;
    if (this.markerById[location.id]) return;
    var marker = window.L.circleMarker([position.lat, position.lng], {
      radius: 8,
      color: '#fffdf9',
      weight: 3,
      fillColor: this.accentColor,
      fillOpacity: 1
    }).addTo(this.map);
    marker.on('click', function () { locator.openMarker(location, marker); });
    this.markers.push(marker);
    this.markerById[location.id] = marker;
    if (bounds) bounds.extend([position.lat, position.lng]);
  };

  StoreLocator.prototype.updateMap = function () {
    if (!this.map) return;
    this.clearMarkers();
    var bounds = window.L.latLngBounds([]);
    var nearbyBounds = window.L.latLngBounds([]);
    var nearbyPositionCount = 0;

    if (this.userPosition) {
      this.updateUserMarker();
      nearbyBounds.extend([this.userPosition.lat, this.userPosition.lng]);
    }

    this.filtered.forEach(function (location) {
      var position = this.locationPosition(location);
      if (!position) return;
      this.addMarker(location, position, bounds);
      if (this.userPosition && nearbyPositionCount < 5) {
        nearbyBounds.extend([position.lat, position.lng]);
        nearbyPositionCount += 1;
      }
    }, this);

    if (this.userPosition && nearbyPositionCount) this.fitBounds(nearbyBounds);
    else if (this.userPosition) this.map.setView([this.userPosition.lat, this.userPosition.lng], 10);
    else if (bounds.isValid()) this.fitBounds(bounds);
    else this.map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], 2);
  };

  StoreLocator.prototype.fitBounds = function (bounds) {
    this.map.fitBounds(bounds, { padding: [70, 70], maxZoom: 14 });
  };

  StoreLocator.prototype.popupContent = function (location) {
    var locationLine = [location.city, location.country].filter(Boolean).join(', ');
    return '<div class="store-locator-popup">' +
      '<strong>' + escapeHtml(location.name) + '</strong>' +
      (location.address ? '<span>' + escapeHtml(location.address) + '</span>' : '<span>' + escapeHtml(locationLine) + '</span>') +
      '</div>';
  };

  StoreLocator.prototype.openMarker = function (location, marker) {
    marker.bindPopup(this.popupContent(location), { maxWidth: 260, closeButton: true }).openPopup();
  };

  StoreLocator.prototype.focusLocation = function (location, card) {
    var locator = this;
    Array.prototype.forEach.call(this.list.querySelectorAll('.is-active'), function (item) { item.classList.remove('is-active'); });
    card.classList.add('is-active');
    if (!this.map) return;
    var marker = this.markerById[location.id];
    if (!marker) return;
    this.map.flyTo(marker.getLatLng(), Math.max(this.map.getZoom(), 14), { duration: 0.45 });
    window.setTimeout(function () { locator.openMarker(location, marker); }, 250);
  };

  StoreLocator.prototype.directionsUrl = function (location) {
    var position = this.locationPosition(location);
    if (position && this.userPosition) {
      return 'https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=' + this.userPosition.lat + '%2C' + this.userPosition.lng + '%3B' + position.lat + '%2C' + position.lng;
    }
    return 'https://www.openstreetmap.org/search?query=' + encodeURIComponent(this.locationAddress(location));
  };

  function initStoreLocators() {
    document.querySelectorAll('[data-store-locator]').forEach(function (root) {
      if (!root.dataset.initialized) {
        root.dataset.initialized = 'true';
        new StoreLocator(root);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStoreLocators);
  else initStoreLocators();
  document.addEventListener('shopify:section:load', initStoreLocators);
})();
